import { searchLogger, searchErrorLogger } from '@/core/Logger'
import { createApp, ref } from 'vue'
import AutocompletePopup from './components/AutocompletePopup/AutocompletePopup.vue'
import SearchInput from './components/SearchOnlyInput.vue'
import SerpPage from './components/SerpPage/SerpPage.vue'

import { SmartSearchConfigInterface, SmartSearchTranslationsInterface } from './core/types'
import {
  restoreDefaultSettings,
  fetchFrontSettings,
  searchIsConfigured,
  deepAssign,
  showAsStylisationList,
  overrideSettings
} from './core/utils'
import { createPinia } from 'pinia'
import DefaultConfig from '../public/config/DefaultConfig.json'
import DefaultLabels from '../public/labels/DefaultLabels.json'

import VueLazyload from 'vue-lazyload'
import ClickOutsideDirective from '@/core/ClickOutsideDirective'
import { createAutocompleteBridge } from '@/core/bridge'

const { VUE_APP_VERSION } = process.env

const SmartSearchConfig = ref<SmartSearchConfigInterface>(DefaultConfig)
const SmartSearchTranslations = ref<SmartSearchTranslationsInterface>(DefaultLabels)

searchLogger.log(`Version: ${VUE_APP_VERSION}`)

const pinia = createPinia()
const autocompleteBridge = createAutocompleteBridge({
  pinia,
  smartSearchConfig: SmartSearchConfig,
  smartSearchTranslations: SmartSearchTranslations
})

// Globalny storage
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer = window._QONizer || {}
if (window._QONizer._initialized === undefined) {
  window._QONizer._initialized = {
    autocomplete: false,
    searchInput: false,
    serpPage: false
  }
}

// Flaga czy config już załadowany
let configReady = false

/**
 * Czy dany selector ma już zamontowaną aplikację Vue
 */
function isMounted (selector: string): boolean {
  const element = document.querySelector(selector)
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return !!(element && element.__vue_app__)
}

/**
 * Wspólna walidacja przed montowaniem
 */
function canMount (flagKey: 'autocomplete' | 'searchInput' | 'serpPage', selector: string): boolean {
  if (!configReady) {
    return false
  }
  
  if (window._QONizer._initialized[flagKey]) {
    return false
  }
  
  if (!document.querySelector(selector)) {
    return false
  }
  
  return true
}

/**
 * Inicjalizacja AutocompletePopup
 */
async function initializeAutocomplete (selector = '#smartsearch-autocomplete') {
  if (!canMount('autocomplete', selector)) return
  
  searchLogger.log('Initialising Autocomplete')
  const autocompleteApp = createApp(AutocompletePopup)
  
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  autocompleteApp.component('searchInput', SmartSearchConfig.value.serp.customComponents.topProduct)
  autocompleteApp.directive('click-outside', ClickOutsideDirective)
  autocompleteApp.provide('SmartSearchConfig', SmartSearchConfig)
  autocompleteApp.provide('SmartSearchTranslations', SmartSearchTranslations)
  autocompleteApp.use(pinia)
  autocompleteApp.use(VueLazyload)
  autocompleteApp.mount(selector)
  
  if (isMounted(selector)) {
    searchLogger.log('Autocomplete mounted successfully')
    window._QONizer._initialized.autocomplete = true
  }
}

/**
 * Inicjalizacja SearchInput
 */
async function initializeSearchInput (selector = '#smartsearch-searchInput') {
  if (!canMount('searchInput', selector)) return
  
  searchLogger.log('Initialising SearchInput')
  const SearchInputApp = createApp(SearchInput)
  SearchInputApp.directive('click-outside', ClickOutsideDirective)
  SearchInputApp.provide('SmartSearchConfig', SmartSearchConfig)
  SearchInputApp.provide('SmartSearchTranslations', SmartSearchTranslations)
  SearchInputApp.use(pinia)
  SearchInputApp.use(VueLazyload)
  SearchInputApp.mount(selector)
  
  if (isMounted(selector)) {
    searchLogger.log('SearchInput mounted successfully')
    window._QONizer._initialized.searchInput = true
  }
}

/**
 * Inicjalizacja SerpPage
 */
async function initializeSerpPage (selector = '#smartsearch-serp') {
  if (!canMount('serpPage', selector)) return
  
  searchLogger.log('Initialising SerpPage')
  const serpApp = createApp(SerpPage)
  serpApp.directive('click-outside', ClickOutsideDirective)
  serpApp.provide('SmartSearchConfig', SmartSearchConfig)
  serpApp.provide('SmartSearchTranslations', SmartSearchTranslations)
  serpApp.use(pinia)
  serpApp.use(VueLazyload)
  serpApp.mount(selector)
  
  if (isMounted(selector)) {
    searchLogger.log('SerpPage mounted successfully')
    window._QONizer._initialized.serpPage = true
  }
}

/**
 * Próba montażu wszystkich brakujących komponentów
 */
async function attemptMountAll () {
  await initializeAutocomplete()
  await initializeSearchInput()
  await initializeSerpPage()
}

/**
 * Po załadowaniu configu nasłuchujemy DOM aż pojawią się brakujące kontenery.
 */
function startMutationObserver () {
  const observer = new MutationObserver(() => {
    attemptMountAll()
    if (
      window._QONizer._initialized.autocomplete &&
      window._QONizer._initialized.searchInput &&
      window._QONizer._initialized.serpPage
    ) {
      observer.disconnect()
    }
  })
  observer.observe(document.documentElement, { childList: true, subtree: true })
}

// Globalne API do ręcznego montażu
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.initializeAutocomplete = (selector = '#smartsearch-autocomplete') => initializeAutocomplete(selector)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.initializeSerpPage = (selector = '#smartsearch-serp') => initializeSerpPage(selector)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.initializeSearchInput = (selector = '#smartsearch-searchInput') => initializeSearchInput(selector)
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.autocomplete = window._QONizer.autocomplete || {}
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.autocomplete.bindInputBridge = autocompleteBridge.bindInputBridge
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.autocomplete.bindSearchButton = autocompleteBridge.bindSearchButton
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.autocomplete.bindTrigger = autocompleteBridge.bindTrigger
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
window._QONizer.autocomplete.focusAfterClick = autocompleteBridge.focusAfterClick

/**
 * Główne ładowanie configu + inicjalizacja
 */
;(async () => {
  const decodeHtmlEntities = (input: string): string => {
    if (typeof input !== 'string') {
      return input as unknown as string
    }

    const decodeOnce = (value: string): string => {
      if (typeof document === 'undefined') {
        return value
          .replace(/&amp;/g, '&')
          .replace(/&lt;/g, '<')
          .replace(/&gt;/g, '>')
          .replace(/&quot;/g, '"')
          .replace(/&#039;/g, "'")
      }

      const textarea = document.createElement('textarea')
      textarea.innerHTML = value
      return textarea.value
    }

    let prev = input
    let decoded = decodeOnce(prev)
    let safety = 0
    while (decoded !== prev && safety < 5) {
      prev = decoded
      decoded = decodeOnce(prev)
      safety += 1
    }

    return decoded
  }

  const decodeLabels = (input: unknown): unknown => {
    if (typeof input === 'string') {
      return decodeHtmlEntities(input)
    }

    if (Array.isArray(input)) {
      return input.map((entry) => decodeLabels(entry))
    }

    if (input && typeof input === 'object') {
      return Object.entries(input as Record<string, unknown>).reduce((acc, [key, val]) => {
        acc[key] = decodeLabels(val)
        return acc
      }, {} as Record<string, unknown>)
    }

    return input
  }

  if (!searchIsConfigured()) {
    searchErrorLogger.log('SmartSearch needs config in window.SmartSearchConfig')
    return
  }
  
  try {
    const frontSettings = await fetchFrontSettings()
    if (!frontSettings || !frontSettings.settings) {
      throw new Error('Brak danych w odpowiedzi frontSettings')
    }
    
    const { settings, labels } = frontSettings
    const mergedConfig = deepAssign()(DefaultConfig, settings || {})
    const mergedLabels = deepAssign()(DefaultLabels, labels || {})
    const decodedLabels = decodeLabels(mergedLabels) as SmartSearchTranslationsInterface
    
    SmartSearchTranslations.value = decodedLabels
    SmartSearchConfig.value = mergedConfig as SmartSearchConfigInterface
    
    // Jeśli restoreDefaultSettings nadpisuje config – przenieś ją wyżej albo usuń
    restoreDefaultSettings()
    showAsStylisationList()
    overrideSettings()
    
    // Aktualizacja globalnego configu po ostatecznym merge
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.SmartSearchInstanceConfig = SmartSearchConfig.value
    
    searchLogger.log('SmartSearchTranslations: ', SmartSearchTranslations.value)
    searchLogger.log('SmartSearchConfig: ', SmartSearchConfig.value)
    
    configReady = true
    attemptMountAll()
    startMutationObserver()
  } catch (e) {
    searchErrorLogger.log('Nie udało się pobrać frontSettings – aplikacja nie zostanie uruchomiona.', e)
    return
  }
})()

export {
  SmartSearchConfig,
  SmartSearchTranslations,
  initializeAutocomplete,
  initializeSearchInput,
  initializeSerpPage
}
